/**
 * Host push registration — the ONLY module in the app that imports
 * `@capacitor/push-notifications` (phase6-contract.md §3.5.1).
 *
 * The plugin is used for exactly three things: `register()` / `unregister()`
 * and the four listeners — and only in a build whose native shells carry a
 * push-provider configuration (`NATIVE_PUSH_ENABLED`, DEC-105): every call sits
 * behind {@link nativeProviderConfigured}. That gate cannot be a try/catch. On
 * an Android shell without `google-services.json`, `register()` and
 * `unregister()` both start with `FirebaseMessaging.getInstance()`, which
 * throws, and Capacitor's Bridge rethrows a plugin method's exception on its
 * handler thread: the app PROCESS dies, and the error never reaches JS as a
 * rejected promise. Until 0.29.1 that closed the app on every Android sign-out.
 *
 * The plugin is NOT the source of the OS permission — on
 * Android ≤ 12 `PushNotifications.checkPermissions()` always answers
 * `granted`, so the display permission stays with `local-notifications.service`
 * (which also serves the event reminders and the exact-alarm offer). The
 * facade in `notifications.service.ts` owns that gate and calls in here only
 * once the permission is granted.
 *
 * What this module owns:
 *  - the user's PREFERENCE (`syntry.device_notifications.v1` — `enabled |
 *    disabled | unset`): a permission the OS remembers is not consent for
 *    background delivery; only an explicit Enable is (ERPat DEC-059 parity);
 *  - the two registration records (§3.5.1): `syntry.push_registration.v1`
 *    (`{provider, key, activationStreamId, registeredAt}` — for a native
 *    provider `key` is the sha256 of the token, and the TOKEN ITSELF IS NEVER
 *    STORED OR LOGGED) and `syntry.push_invalid.v1` (persists across restarts;
 *    cleared by Enable, Disable, a 201/200 or a denied permission);
 *  - the tenant policy (`policy()`): web = `host_notifications.web_push_available`,
 *    native = `host_notifications.policy === 'opt_in'` (DEC-097 —
 *    `features.host_push` is the WEB flag and is never read for native);
 *  - `ensureRegistration()` under a Web Locks / 15 s lease: native →
 *    `register()` → the `'registration'` event delivers the token → PUT only
 *    when the token hash changed or no record exists, AND the preference is
 *    still `enabled`, a bearer is still cached and the session epoch is the one
 *    the call started with (a late token while not enabled is ignored); web →
 *    the worker registration `register.ts` made (production only) →
 *    `pushManager.subscribe` with the bootstrap VAPID key → PUT `web_push`;
 *  - `revoke()` (awaits an in-flight registration → DELETE bounded 3 s →
 *    `unregister()` (native, with a provider only) / `unsubscribe()` → drop
 *    the registration record),
 *    `clearLocal()` (the two records), `refresh()` (un-throttled re-check);
 *  - the worker-message hook point {@link handleWorkerMessage} (W5 wires the
 *    subscription through `register.ts`'s `onWorkerMessage`).
 *
 * The server's exact body (§3.2.4): `platform` is the OS-qualified enum,
 * `environment` is `production` for web and `VITE_PUSH_ENVIRONMENT` for native,
 * `application_id` (`app.syntry`, DEC-081) is REQUIRED for `fcm` / `apns` and
 * ABSENT for `web_push`. A native 422 (the `application_id` allow-list is empty
 * on dev — `DEVICE_PUSH_NATIVE_APPLICATION_IDS`) marks the installation
 * `invalid` until the user taps Enable again (DEC-088).
 *
 * `reconcile()` lives in `realtime.service.ts` (W1) and is resolved lazily so
 * this module never statically closes the realtime → device → realtime cycle.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { RouteLocationRaw } from 'vue-router';
import { getSessionEpoch, hasAccessToken } from '@/services/api/erpat-client';
import {
  RealtimeApiError,
  registerEndpoint,
  revokeEndpoint,
  type EndpointPlatform,
  type EndpointProvider,
  type EndpointRegistration,
  type EndpointRegistrationBody,
  type RealtimeBootstrap,
  type WebPushCredential,
} from '@/services/api/realtime.api';
import { tenantStamp } from '@/services/session-scope';
import { REALTIME_ACTION_ROUTES, type RealtimeAction, type RealtimeActionRoute } from '@/contracts/realtime-envelope-v1';
import { resolveRealtimeRoute } from '@/contracts/realtime-routes';
import { isPwaSupported, onWorkerMessage } from '@/pwa/register';
import { HOST_NOTIFICATIONS_ENABLED, IS_DEV, NATIVE_PUSH_ENABLED, PUSH_ENVIRONMENT } from '@/utils/env';

/* ── Keys and constants ────────────────────────────────────────────────────── */

/** `localStorage` — the user's device-notification preference (§3.5.2). */
export const DEVICE_PREFERENCE_KEY = 'syntry.device_notifications.v1';
/** `localStorage` — the registration the server accepted for this installation (§3.5.1). */
export const PUSH_REGISTRATION_KEY = 'syntry.push_registration.v1';
/** `localStorage` — `'1'` while the last registration was refused with 422 (§3.5.1). */
export const PUSH_INVALID_KEY = 'syntry.push_invalid.v1';
/** The native application id the server allow-lists (`capacitor.config.ts` `appId`, DEC-081). */
export const NATIVE_APPLICATION_ID = 'app.syntry';

/** The message the worker receives after a registration (§3.8). */
export const SW_CONFIG_MESSAGE_TYPE = 'syntry:sw-config';

/** A recheck-driven `ensureRegistration()` runs at most once per this interval (§3.5.2). */
export const RECHECK_THROTTLE_MS = 60_000;
/** The registration lease (Web Locks, else a `localStorage` stamp) and the native token wait. */
const LOCK_LEASE_MS = 15_000;
const LOCK_NAME = 'syntry-push-registration';
const LOCK_KEY = 'syntry.push_registration_lock.v1';
/** `revoke()` waits this long for the server's DELETE before moving on (DEC-090). */
const REVOKE_TIMEOUT_MS = 3_000;

/* ── Types ─────────────────────────────────────────────────────────────────── */

export type DevicePreference = 'unset' | 'enabled' | 'disabled';
/** The endpoint axis of the five-way model (§3.5.2). */
export type PushState = 'unavailable' | 'off' | 'active' | 'invalid';
/** The tenant-policy axis of the five-way model (§3.5.2). */
export type HostPolicy = 'off' | 'available';
/** The bootstrap's `host_notifications` block, as `realtime.api.ts` maps it. */
export type HostNotificationsInfo = RealtimeBootstrap['hostNotifications'];

export interface PushRegistrationRecord {
  provider: EndpointProvider;
  /** Web: the VAPID public key subscribed with. Native: the sha256 hex of the token. */
  key: string;
  activationStreamId: string | null;
  /** RFC 3339 UTC second. */
  registeredAt: string;
}

/** What a tapped push (native action, worker click) hands the facade. */
export interface PushTap {
  target: RouteLocationRaw;
  eventId: string;
  /** The tenant stamp carried by the payload / worker message ('' when absent). */
  tenant: string;
}

/* ── Module state ──────────────────────────────────────────────────────────── */

let host: HostNotificationsInfo | null = null;
let registering: Promise<void> | null = null;
let revoking = false;
let lastEnsureAt = 0;
let nativeReady = false;
let nativeAttaching: Promise<void> | null = null;
let detachNative: Array<() => void> = [];
let tokenWaiter: { resolve(token: string): void; fail(): void } | null = null;
let tapHandler: ((tap: PushTap) => void) | null = null;
/** The `register.ts` worker-message subscription (§3.8) — attached in `prepare()`, released in `release()`. */
let unsubscribeWorker: (() => void) | null = null;
/** Dev-only force hook state (compiled out of production with the hook itself). */
let forcedAvailable: boolean | null = null;
let forcedKey: string | null = null;

function warn(scope: string, err?: unknown): void {
  if (IS_DEV) console.warn(`[push] ${scope}`, err === undefined ? '' : err);
}

/* ── Storage helpers ───────────────────────────────────────────────────────── */

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / blocked storage: nothing is remembered past this page */
  }
}

function storageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** The user's preference for THIS device. `unset` until an explicit Enable/Disable. */
export function readPreference(): DevicePreference {
  const value = storageGet(DEVICE_PREFERENCE_KEY);
  return value === 'enabled' || value === 'disabled' ? value : 'unset';
}

export function writePreference(value: 'enabled' | 'disabled'): void {
  storageSet(DEVICE_PREFERENCE_KEY, value);
}

/** Back to `unset` — on a denied permission (ERPat parity). */
export function clearPreference(): void {
  storageRemove(DEVICE_PREFERENCE_KEY);
}

function isProvider(value: unknown): value is EndpointProvider {
  return value === 'web_push' || value === 'fcm' || value === 'apns';
}

/** The registration the server accepted, or `null`. Never carries a token. */
export function readRegistrationRecord(): PushRegistrationRecord | null {
  const raw = storageGet(PUSH_REGISTRATION_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const rec = parsed as Record<string, unknown>;
    if (!isProvider(rec.provider) || typeof rec.key !== 'string' || rec.key === '') return null;
    return {
      provider: rec.provider,
      key: rec.key,
      activationStreamId: typeof rec.activationStreamId === 'string' ? rec.activationStreamId : null,
      registeredAt: typeof rec.registeredAt === 'string' ? rec.registeredAt : '',
    };
  } catch {
    return null;
  }
}

function writeRegistrationRecord(record: PushRegistrationRecord): void {
  storageSet(PUSH_REGISTRATION_KEY, JSON.stringify(record));
}

function clearRegistrationRecord(): void {
  storageRemove(PUSH_REGISTRATION_KEY);
}

/** True while the last registration attempt was refused with 422 (§3.5.1). */
export function isInvalid(): boolean {
  return storageGet(PUSH_INVALID_KEY) === '1';
}

function markInvalid(): void {
  storageSet(PUSH_INVALID_KEY, '1');
}

/** Forget the `invalid` mark — Enable, Disable, a 201/200 or a denied permission. */
export function clearInvalid(): void {
  storageRemove(PUSH_INVALID_KEY);
}

/** Remove the two records (§3.5.1) — sign-out, and a failed worker re-subscription. */
export function clearLocal(): void {
  clearRegistrationRecord();
  clearInvalid();
  lastEnsureAt = 0;
}

/* ── Policy and support ────────────────────────────────────────────────────── */

/**
 * Adopt the bootstrap's `host_notifications` block (the realtime service calls
 * this — or the facade's `applyBootstrap()` — once a bootstrap answered; `null`
 * forgets it on stop). Nothing here is read before it arrives: `policy()` is
 * `off` until then, so a registration can never run against a guessed key.
 */
export function setHostNotifications(info: HostNotificationsInfo | null): void {
  host = info;
}

/** The last adopted bootstrap block, or `null`. */
export function hostNotifications(): HostNotificationsInfo | null {
  return host;
}

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * True when this build may call the push provider (DEC-105): the build says its
 * native shells carry a provider configuration, and the plugin is registered.
 * `isPluginAvailable()` alone proves nothing — an Android shell registers the
 * plugin with or without the Firebase configuration its provider calls need.
 * Every `PushNotifications.*` call sits behind this; the header says why.
 */
function nativeProviderConfigured(): boolean {
  return NATIVE_PUSH_ENABLED && Capacitor.isPluginAvailable('PushNotifications');
}

/** True when the platform can hold a push registration at all. */
export function pushSupported(): boolean {
  if (!HOST_NOTIFICATIONS_ENABLED) return false;
  // Native answers here and never falls through to the web checks below: the
  // Android WebView has a service worker, PushManager and a secure context too.
  if (isNative()) return nativeProviderConfigured();
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    window.isSecureContext === true
  );
}

/**
 * The tenant policy axis (§3.5.1, DEC-097): web reads `web_push_available`,
 * native reads `policy === 'opt_in'`. The build flag turns both off.
 */
export function policy(): HostPolicy {
  if (!HOST_NOTIFICATIONS_ENABLED) return 'off';
  if (forcedAvailable !== null) return forcedAvailable ? 'available' : 'off';
  if (!host) return 'off';
  if (isNative()) return host.policy === 'opt_in' ? 'available' : 'off';
  return host.webPushAvailable ? 'available' : 'off';
}

/** The endpoint axis (§3.5.2): unavailable → invalid → active → off. */
export function pushState(): PushState {
  if (!pushSupported() || policy() === 'off') return 'unavailable';
  if (isInvalid()) return 'invalid';
  return readRegistrationRecord() !== null ? 'active' : 'off';
}

/** True while this installation holds a registration the server accepted. */
export function pushActive(): boolean {
  return readRegistrationRecord() !== null;
}

function nativeProvider(): EndpointProvider {
  return Capacitor.getPlatform() === 'ios' ? 'apns' : 'fcm';
}

/** The server's exact `platform` enum (§3.2.4). */
export function platformOf(): EndpointPlatform {
  if (isNative()) return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
  const data = (navigator as Navigator & { userAgentData?: { platform?: unknown } }).userAgentData;
  const raw = data && typeof data.platform === 'string' ? data.platform : navigator.platform || '';
  const name = raw.toLowerCase();
  if (name.includes('win')) return 'web_windows';
  if (name.includes('mac')) return 'web_macos';
  if (name.includes('android')) return 'web_android';
  if (name.includes('iphone') || name.includes('ipad') || name.includes('ios')) return 'web_ios';
  return 'web_linux';
}

function localeOf(): string {
  return typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
}

function timezoneOf(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** RFC 3339 UTC second — the shape the receipts and records use. */
function nowIso(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/* ── Hashing (the native token is stored only as its digest) ───────────────── */

function fnv1a64Hex(text: string): string {
  // Two 32-bit lanes over the UTF-16 code units — a stable fallback digest for
  // a runtime without Web Crypto; prefixed so it is never mistaken for sha256.
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ (c + i), 0x01000193) >>> 0;
  }
  return `fnv1a:${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

/** sha256 hex of `text` (Web Crypto), with a clearly-labelled fallback digest. */
export async function sha256Hex(text: string): Promise<string> {
  const subtle = typeof globalThis.crypto !== 'undefined' ? globalThis.crypto.subtle : undefined;
  if (subtle && typeof subtle.digest === 'function') {
    try {
      const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
      return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    } catch {
      /* fall through */
    }
  }
  return fnv1a64Hex(text);
}

/* ── The registration lease ────────────────────────────────────────────────── */

/**
 * Two tabs (or two rapid calls) never subscribe at once: the Web Locks API
 * when present (`ifAvailable`, so a busy lock skips rather than queues), else
 * a 15 s `localStorage` lease. A skipped run resolves to `false`.
 */
async function withLock(fn: () => Promise<void>): Promise<boolean> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks && typeof locks.request === 'function') {
    return locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => {
      if (!lock) return false;
      await fn();
      return true;
    });
  }
  const now = Date.now();
  const lease = parseInt(storageGet(LOCK_KEY) ?? '0', 10);
  if (lease && now - lease < LOCK_LEASE_MS) return false;
  storageSet(LOCK_KEY, String(now));
  try {
    await fn();
    return true;
  } finally {
    storageRemove(LOCK_KEY);
  }
}

/* ── Registration answers ──────────────────────────────────────────────────── */

function applyRegistration(answer: EndpointRegistration, provider: EndpointProvider, key: string): void {
  // 201 (first registration / cross-user rebind) and 200 (refresh) both mean
  // the server holds this endpoint: the `invalid` mark is stale either way.
  clearInvalid();
  writeRegistrationRecord({
    provider: answer.provider ?? provider,
    key,
    activationStreamId: answer.activationStreamId,
    registeredAt: nowIso(),
  });
}

function applyRegistrationFailure(err: unknown): void {
  if (err instanceof RealtimeApiError) {
    if (err.status === 404) {
      // Push is not offered (deployment / module off): the permission stays,
      // the record goes, and we stop asking until the policy says otherwise.
      clearRegistrationRecord();
      return;
    }
    if (err.status === 422) {
      // The credential or (native) the application_id allow-list was refused:
      // `invalid` until the user taps Enable again (DEC-088).
      clearRegistrationRecord();
      markInvalid();
      return;
    }
    warn(`endpoint registration answered ${err.code} (${err.status})`);
    return;
  }
  warn('endpoint registration failed', err);
}

/* ── Native (fcm / apns) ───────────────────────────────────────────────────── */

function nativeBody(token: string): EndpointRegistrationBody {
  return {
    provider: nativeProvider(),
    platform: platformOf(),
    environment: PUSH_ENVIRONMENT,
    application_id: NATIVE_APPLICATION_ID,
    locale: localeOf(),
    timezone: timezoneOf(),
    credential: { token },
  };
}

/**
 * A token from the `'registration'` event — solicited (inside
 * `ensureRegistration()`, with the epoch it started under) or unsolicited (an
 * FCM/APNs rotation). Every guard of §3.5.1 runs here, so a token that lands
 * after a Disable, a sign-out or a company switch is ignored.
 */
async function onNativeToken(token: string, epoch: number | null): Promise<void> {
  if (readPreference() !== 'enabled' || !hasAccessToken()) return;
  if (epoch !== null && getSessionEpoch() !== epoch) return;
  if (policy() === 'off') return;
  const provider = nativeProvider();
  const key = await sha256Hex(token);
  // The digest is async: re-run the identity guards after it.
  if (readPreference() !== 'enabled' || !hasAccessToken()) return;
  if (epoch !== null && getSessionEpoch() !== epoch) return;
  const record = readRegistrationRecord();
  if (record && record.provider === provider && record.key === key) return;
  const sessionEpoch = getSessionEpoch();
  try {
    const answer = await registerEndpoint(nativeBody(token));
    // Signed out while the POST was in flight (revoke() stops waiting after 3 s):
    // the record belongs to a session that is gone.
    if (getSessionEpoch() !== sessionEpoch) return;
    applyRegistration(answer, provider, key);
  } catch (err) {
    applyRegistrationFailure(err);
  }
}

function parseTapData(raw: unknown): { target: RouteLocationRaw; eventId: string; tenant: string } {
  const data = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const eventId = typeof data.event_id === 'string' ? data.event_id : typeof data.eventId === 'string' ? data.eventId : '';
  const tenantRaw = typeof data.tenant_scope === 'string' ? data.tenant_scope : typeof data.tenant === 'string' ? data.tenant : '';
  let action: RealtimeAction | null = null;
  if (typeof data.route === 'string' && (REALTIME_ACTION_ROUTES as readonly string[]).includes(data.route)) {
    const params: Record<string, string> = {};
    const rawParams = data.params && typeof data.params === 'object' ? (data.params as Record<string, unknown>) : {};
    for (const [key, value] of Object.entries(rawParams)) {
      if (typeof value === 'string') params[key] = value;
      else if (typeof value === 'number' && Number.isFinite(value)) params[key] = String(value);
    }
    action = { route: data.route as RealtimeActionRoute, params };
  }
  return { target: resolveRealtimeRoute(action), eventId, tenant: tenantRaw.trim().toLowerCase() };
}

/** Attach the four plugin listeners once (native, with a provider configuration only). */
async function attachNativeListeners(): Promise<void> {
  if (nativeReady || !isNative() || !nativeProviderConfigured()) return;
  if (nativeAttaching) return nativeAttaching;
  nativeAttaching = (async () => {
    try {
      const handles = await Promise.all([
        PushNotifications.addListener('registration', (token) => {
          const value = typeof token.value === 'string' ? token.value : '';
          if (value === '') return;
          if (tokenWaiter) {
            tokenWaiter.resolve(value);
            return;
          }
          // An unsolicited rotation: same guards, current epoch.
          void onNativeToken(value, null);
        }),
        PushNotifications.addListener('registrationError', () => {
          // The provider refused this installation: `invalid` until Enable.
          clearRegistrationRecord();
          markInvalid();
          tokenWaiter?.fail();
        }),
        PushNotifications.addListener('pushNotificationReceived', () => {
          // Foreground delivery (presentationOptions: []): no OS alert — the
          // app is live and shows the event itself; make sure it is fetched.
          reconcileLazy('push_received');
        }),
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const tap = parseTapData(action.notification.data);
          tapHandler?.(tap);
        }),
      ]);
      detachNative = handles.map((handle) => () => {
        void handle.remove().catch(() => undefined);
      });
      nativeReady = true;
    } catch (err) {
      warn('addListener', err);
    } finally {
      nativeAttaching = null;
    }
  })();
  return nativeAttaching;
}

/** `register()` and wait for the `'registration'` event (bounded by the lease). */
function requestNativeToken(): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (token: string | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      tokenWaiter = null;
      resolve(token);
    };
    const timer = setTimeout(() => finish(null), LOCK_LEASE_MS);
    tokenWaiter = { resolve: (token) => finish(token), fail: () => finish(null) };
    PushNotifications.register().catch((err: unknown) => {
      warn('register', err);
      finish(null);
    });
  });
}

async function registerNativeFlow(epoch: number): Promise<void> {
  await attachNativeListeners();
  if (!nativeReady) return;
  const token = await requestNativeToken();
  if (token === null) return;
  await onNativeToken(token, epoch);
}

/* ── Web (web_push) ────────────────────────────────────────────────────────── */

function base64UrlToBytes(value: string): Uint8Array {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function sameKey(subscription: PushSubscription, keyBytes: Uint8Array): boolean {
  const existing = subscription.options?.applicationServerKey;
  if (!existing) return false;
  const view = new Uint8Array(existing);
  if (view.length !== keyBytes.length) return false;
  for (let i = 0; i < view.length; i += 1) if (view[i] !== keyBytes[i]) return false;
  return true;
}

/**
 * The worker registration `register.ts` made — never registered from here. In
 * dev (no worker is ever registered) only an existing registration counts,
 * so the dev-only force hook needs a stubbed `getRegistration()`.
 */
async function workerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) return existing;
    if (!isPwaSupported()) return null;
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/** Hand the worker the VAPID key and the tenant stamp it stamps into `data.tenant` (§3.8, DEC-094). */
function postWorkerConfig(registration: ServiceWorkerRegistration, key: string): void {
  const worker = registration.active ?? registration.waiting ?? registration.installing;
  if (!worker || typeof worker.postMessage !== 'function') return;
  try {
    worker.postMessage({ type: SW_CONFIG_MESSAGE_TYPE, vapid: key, tenant: tenantStamp() });
  } catch (err) {
    warn('worker config not delivered', err);
  }
}

async function subscribeWith(registration: ServiceWorkerRegistration, keyBytes: Uint8Array): Promise<PushSubscription> {
  const existing = await registration.pushManager.getSubscription();
  if (existing && sameKey(existing, keyBytes)) return existing;
  if (existing) await existing.unsubscribe().catch(() => undefined);
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    // A copy on an ArrayBuffer: the lib types want a BufferSource, and a view
    // over a SharedArrayBuffer-typed slice is rejected by TS 5.7+.
    applicationServerKey: new Uint8Array(keyBytes).buffer as ArrayBuffer,
  });
}

/** `expirationTime` is epoch ms (or null); the server takes an RFC 3339 second or null. */
function expirationOf(raw: unknown): string | null {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return new Date(raw).toISOString().replace(/\.\d{3}Z$/, 'Z');
  }
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

function credentialOf(subscription: PushSubscription): WebPushCredential {
  const json = typeof subscription.toJSON === 'function' ? subscription.toJSON() : {};
  const keys = json.keys ?? {};
  return {
    endpoint: json.endpoint ?? subscription.endpoint,
    expiration_time: expirationOf(json.expirationTime === undefined ? subscription.expirationTime : json.expirationTime),
    keys: { p256dh: keys.p256dh ?? '', auth: keys.auth ?? '' },
  };
}

function webBody(subscription: PushSubscription): EndpointRegistrationBody {
  return {
    provider: 'web_push',
    platform: platformOf(),
    // Web always registers against production (Phase 5 parity) — the VAPID
    // key pair is the deployment's, there is no sandbox to speak of.
    environment: 'production',
    locale: localeOf(),
    timezone: timezoneOf(),
    credential: credentialOf(subscription),
  };
}

async function registerWebFlow(epoch: number): Promise<void> {
  const key = forcedKey ?? host?.vapidPublicKey ?? null;
  if (!key) return;
  let keyBytes: Uint8Array;
  try {
    keyBytes = base64UrlToBytes(key);
  } catch {
    warn('vapid key is not base64url');
    return;
  }
  const registration = await workerRegistration();
  if (!registration) return;
  postWorkerConfig(registration, key);
  const subscription = await subscribeWith(registration, keyBytes);
  if (readPreference() !== 'enabled' || !hasAccessToken() || getSessionEpoch() !== epoch) {
    // Disabled or signed out while subscribing: undo, never register.
    await subscription.unsubscribe().catch(() => undefined);
    return;
  }
  try {
    const answer = await registerEndpoint(webBody(subscription));
    // Signed out while the POST was in flight (revoke() stops waiting after 3 s):
    // the record belongs to a session that is gone.
    if (getSessionEpoch() !== epoch) return;
    applyRegistration(answer, 'web_push', key);
    // The worker's config once more, AFTER the server accepted the endpoint
    // (§3.5.1): a worker that was replaced while we subscribed still ends up
    // holding the key and the tenant it stamps into `data.tenant`.
    postWorkerConfig(registration, key);
  } catch (err) {
    applyRegistrationFailure(err);
  }
}

/* ── Public lifecycle ──────────────────────────────────────────────────────── */

/**
 * Boot-time preparation (§3.7 — attached at boot, not at realtime start, so a
 * cold-start tap is parked rather than lost): the native listeners plus the
 * handler a tap is routed to. Idempotent; a no-op on the web apart from
 * remembering the handler.
 */
export function prepare(onTap: (tap: PushTap) => void): void {
  tapHandler = onTap;
  // The worker's messages (§3.8) — one subscription for the module's lifetime;
  // `onWorkerMessage` keys handlers by identity, so a repeat is a no-op.
  if (!unsubscribeWorker) unsubscribeWorker = onWorkerMessage(handleWorkerMessage);
  void attachNativeListeners();
}

/** Undo {@link prepare}: detach the native listeners and forget the handler. */
export function release(): void {
  tapHandler = null;
  unsubscribeWorker?.();
  unsubscribeWorker = null;
  nativeReady = false;
  for (const detach of detachNative) detach();
  detachNative = [];
  tokenWaiter?.fail();
}

/**
 * Make sure this installation holds a registration the server accepted. Never
 * prompts. The caller (the facade) has already established that the OS
 * permission is granted; this checks the rest — the build flags, the platform
 * (native: its provider configuration), the tenant policy, the preference, a
 * cached bearer — and runs single-flight
 * under the lease. `throttled: true` (a recheck) skips when the last run was
 * less than {@link RECHECK_THROTTLE_MS} ago.
 */
export function ensureRegistration(options: { throttled?: boolean } = {}): Promise<void> {
  if (registering) return registering;
  if (revoking) return Promise.resolve();
  if (!pushSupported() || policy() !== 'available') return Promise.resolve();
  if (readPreference() !== 'enabled' || !hasAccessToken()) return Promise.resolve();
  const now = Date.now();
  if (options.throttled && now - lastEnsureAt < RECHECK_THROTTLE_MS) return Promise.resolve();
  lastEnsureAt = now;

  const epoch = getSessionEpoch();
  registering = withLock(() => (isNative() ? registerNativeFlow(epoch) : registerWebFlow(epoch)))
    .then(
      () => undefined,
      (err: unknown) => {
        warn('registration failed', err);
      },
    )
    .finally(() => {
      registering = null;
    });
  return registering;
}

/** {@link ensureRegistration} un-throttled — the worker's `pushsubscriptionchange`. */
export function refresh(): Promise<void> {
  lastEnsureAt = 0;
  return ensureRegistration();
}

/** Resolve once `promise` settles or `ms` has passed, whichever is first. Never rejects. */
async function settleWithin(promise: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, Math.max(0, ms));
  });
  try {
    await Promise.race([promise.then(() => undefined, () => undefined), bound]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Revoke this installation's endpoint: wait for a registration still in
 * flight (Enable, then Disable a second later), DELETE on the server (the two
 * waits together bounded 3 s, best effort — DEC-090), drop the provider-side
 * token / subscription, then the local record. Called by Disable and BEFORE
 * sign-out.
 *
 * The native `unregister()` runs only when {@link nativeProviderConfigured}
 * (DEC-105): without a provider configuration there is no provider token to
 * drop, and on Android the call itself would kill the app process. The server
 * DELETE runs either way.
 */
export async function revoke(): Promise<void> {
  revoking = true;
  try {
    const deadline = Date.now() + REVOKE_TIMEOUT_MS;

    // Let an in-flight registration finish first, so the DELETE lands after its
    // POST — but inside the same budget. A registration can hang where no timeout
    // reaches it (the lock lease, `pushManager.subscribe()`, the native token
    // wait, a POST queued behind a busy server), and waiting on it without a
    // bound held a sign-out on "Signing Out…" for as long as it did. One that
    // completes late cannot write its record into a later session (the epoch
    // guard after `registerEndpoint()`), and sign-out clears the local records.
    if (registering) await settleWithin(registering, deadline - Date.now());

    // Bound the WAIT, never the request (DEC-090 refined): the DELETE is sent
    // with keepalive and keeps going after this returns, so a slow server (or
    // a page that unloads on sign-out) still receives the revocation. Aborting
    // it instead — the earlier shape — threw the revocation away whenever the
    // answer took longer than the bound; the server row then outlived Disable.
    const request = revokeEndpoint().catch((err: unknown) => {
      // Network, no session, no UUID installation id: the server row (if any)
      // stays; the local state still ends up clean.
      warn('revoke', err);
    });
    await settleWithin(request, deadline - Date.now());

    try {
      if (isNative()) {
        if (nativeProviderConfigured()) await PushNotifications.unregister();
      } else if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        await subscription?.unsubscribe();
      }
    } catch (err) {
      warn('unregister', err);
    }
    clearRegistrationRecord();
    lastEnsureAt = 0;
  } finally {
    revoking = false;
  }
}

/* ── Realtime reconcile (lazy — W1's export lands after this module) ───────── */

function reconcileLazy(reason: 'push_received' | 'push_suppressed'): void {
  void import('@/services/realtime.service')
    .then((mod: unknown) => {
      const fn = mod && typeof mod === 'object' ? (mod as Record<string, unknown>).reconcile : undefined;
      if (typeof fn === 'function') (fn as (r: string) => void)(reason);
    })
    .catch(() => undefined);
}

/* ── W5: worker messages ───────────────────────────────────────────────────── */

/**
 * The hook point for the worker's messages (§3.8), subscribed in {@link prepare}
 * through `register.ts`'s `onWorkerMessage(handleWorkerMessage)` (`register.ts`
 * stays a leaf: the subscription runs this way, never the other).
 *
 *   `syntry:push-suppressed`        → reconcile('push_suppressed')
 *   `syntry:push-resubscribed`      → refresh()
 *   `syntry:push-resubscribe-failed`→ clearLocal()
 *   `syntry:notification-click`     → the tap handler (the facade's openTarget,
 *                                     which routes through navigateToRealtimeTarget)
 */
export function handleWorkerMessage(data: unknown): void {
  const rec = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  switch (rec.type) {
    case 'syntry:push-suppressed':
      reconcileLazy('push_suppressed');
      return;
    case 'syntry:push-resubscribed':
      void refresh();
      return;
    // ERPat spells it `push-resubscribe-failed`; the contract's shorthand
    // ("`syntry:push-resubscribed` / `-failed`") admits the other reading —
    // accept both so the worker (W5) and this hook can never disagree.
    case 'syntry:push-resubscribe-failed':
    case 'syntry:push-resubscribed-failed':
      clearLocal();
      return;
    case 'syntry:notification-click': {
      const url = typeof rec.url === 'string' && rec.url !== '' ? rec.url : null;
      const eventId = typeof rec.eventId === 'string' ? rec.eventId : '';
      const tenant = typeof rec.tenant === 'string' ? rec.tenant.trim().toLowerCase() : '';
      tapHandler?.({ target: url ?? resolveRealtimeRoute(null), eventId, tenant });
      return;
    }
    default:
      return;
  }
}

/* ── Dev-only force hook (compiled out of production) ──────────────────────── */

if (IS_DEV && typeof window !== 'undefined') {
  // `window.__syntryPush.force(available, key)`: pretend the deployment offers
  // push, optionally with a stand-in VAPID public key, so the registration path
  // runs against the REAL endpoint routes on a stack without one (DEC-060).
  (window as Window & { __syntryPush?: unknown }).__syntryPush = {
    force(available: boolean | null, key?: string): void {
      forcedAvailable = available === null || available === undefined ? null : Boolean(available);
      forcedKey = typeof key === 'string' && key !== '' ? key : null;
    },
    state(): { push: PushState; policy: HostPolicy; preference: DevicePreference; record: PushRegistrationRecord | null } {
      return { push: pushState(), policy: policy(), preference: readPreference(), record: readRegistrationRecord() };
    },
  };
}

/** Grouped export, mirroring the other device services. */
export const pushService = {
  readPreference,
  writePreference,
  clearPreference,
  readRegistrationRecord,
  isInvalid,
  clearInvalid,
  clearLocal,
  setHostNotifications,
  hostNotifications,
  pushSupported,
  policy,
  pushState,
  pushActive,
  platformOf,
  prepare,
  release,
  ensureRegistration,
  refresh,
  revoke,
  handleWorkerMessage,
};
